import { OrderListQuery } from 'ish-core/models/order-list-query/order-list-query.model';
import { Order } from 'ish-core/models/order/order.model';

/** Bill-to codes excluded from customer order list (MVP 4.1.1). */
export const PRESIDIO_EXCLUDED_BILL_TO_CODES = new Set(['PRESI072', 'PRESI075', 'PLEDG001']);

export function isPresidioOrderExcluded(order: Order): boolean {
  if (order.presidioHidden) {
    return true;
  }
  const billTo = (order.presidioBillToCode || '').trim().toUpperCase();
  if (billTo && PRESIDIO_EXCLUDED_BILL_TO_CODES.has(billTo)) {
    return true;
  }
  return !!order.presidioHasFinderFee;
}

function includesIgnoreCase(haystack: string | undefined, needle: string | undefined): boolean {
  if (!needle?.trim()) {
    return true;
  }
  return (haystack || '').toLowerCase().includes(needle.trim().toLowerCase());
}

/** Strip urn_ prefix so address id and urn compare as the same ship-to. */
function normalizeShipToToken(value: string): string {
  return (value || '').trim().toLowerCase().replace(/^urn[_:]?/, '');
}

/** Collect order ship-to id/urn variants for address-dropdown filtering. */
function orderShipToRefs(order: Order): string[] {
  const refs = new Set<string>();
  const push = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
      refs.add(value.trim());
    } else if (value && typeof value === 'object') {
      const obj = value as { id?: string; urn?: string };
      if (obj.id?.trim()) {
        refs.add(obj.id.trim());
      }
      if (obj.urn?.trim()) {
        refs.add(obj.urn.trim());
      }
    }
  };
  push((order as Order & { commonShipToAddress?: unknown }).commonShipToAddress);
  push(order.buckets?.[0]?.shipToAddress);
  return [...refs];
}

function matchesAny(haystack: string | undefined, needles: string[] | undefined): boolean {
  if (!needles?.length) {
    return true;
  }
  const h = (haystack || '').toLowerCase();
  return needles.some(n => h.includes((n || '').toLowerCase()));
}

/**
 * MVP 4.1.1 exclusions + 4.1.2 client refine for LOCAL_MOCK (ICM server filter pending).
 */
export function applyPresidioOrderListRules(orders: Order[], query?: Partial<OrderListQuery>): Order[] {
  let result = (orders || []).filter(o => !isPresidioOrderExcluded(o));

  if (!query) {
    return result;
  }

  if (query.documentNumber?.length) {
    result = result.filter(o => matchesAny(o.documentNo, query.documentNumber));
  }
  if (query.lineItem_product?.length) {
    result = result.filter(o => {
      const skus = [
        ...(o.lineItems || []).map(li => li.productSKU),
        ...(o.buckets || []).flatMap(b => (b.lineItems || []).map(li => li.productSKU)),
      ];
      return query.lineItem_product!.some(sku => skus.some(s => includesIgnoreCase(s, sku)));
    });
  }
  if (query.creationDateFrom) {
    const from = Date.parse(query.creationDateFrom);
    if (!Number.isNaN(from)) {
      result = result.filter(o => Date.parse(o.creationDate) >= from);
    }
  }
  if (query.creationDateTo) {
    const to = Date.parse(query.creationDateTo);
    if (!Number.isNaN(to)) {
      // inclusive end-of-day-ish: compare date strings as-is
      result = result.filter(o => Date.parse(o.creationDate) <= to + 24 * 60 * 60 * 1000 - 1);
    }
  }
  if (query.poNumber?.trim()) {
    result = result.filter(o => includesIgnoreCase(o.externalOrderReference || o.presidioPoNumber, query.poNumber));
  }
  if (query.quoteNumber?.trim()) {
    result = result.filter(o => includesIgnoreCase(o.presidioQuoteNumber, query.quoteNumber));
  }
  if (query.status?.trim()) {
    result = result.filter(
      o =>
        includesIgnoreCase(o.status, query.status) || includesIgnoreCase(o.statusCode, query.status)
    );
  }
  if (query.shipToLocation?.trim()) {
    const needle = normalizeShipToToken(query.shipToLocation);
    result = result.filter(o => {
      // Legacy free-text location label (still supported).
      if (includesIgnoreCase(o.presidioShipToLocation, query.shipToLocation)) {
        return true;
      }
      // Address-book dropdown uses id/urn — exact token match (urn_ prefix stripped).
      return orderShipToRefs(o).some(ref => normalizeShipToToken(ref) === needle);
    });
  }
  if (query.affiliatedCompany?.trim()) {
    result = result.filter(o => includesIgnoreCase(o.presidioAffiliatedCompany, query.affiliatedCompany));
  }
  if (query.title?.trim()) {
    result = result.filter(o => includesIgnoreCase(o.presidioTitle, query.title));
  }

  return result;
}
